import { apiCall } from './api';
import {
  OnboardingStatusSchema,
  PhotoUploadResponseSchema,
  PublicTutorViewSchema,
  TutorProfileResponseSchema,
  TutorSearchResponseSchema,
  type CreateTutorProfileInput,
  type OnboardingStatus,
  type PhotoUploadRequestInput,
  type PhotoUploadResponse,
  type PublicTutorView,
  type TutorProfileResponse,
  type TutorSearchQuery,
  type TutorSearchResponse,
  type UpdateTutorProfileInput,
} from './schemas/tutor-profile';

/**
 * Thin client wrappers over the portal-backend `/tutors/*` endpoints. Parsing
 * the responses through the mirrored Zod schemas catches drift early — if the
 * backend changes shape, the page submits crash loudly instead of silently
 * showing the wrong thing.
 */

export async function fetchMyTutorProfile(
  accessToken: string,
): Promise<TutorProfileResponse | null> {
  const res = await apiCall<unknown>('/tutors/me', { accessToken });
  if (res.data === null || res.data === undefined) return null;
  return TutorProfileResponseSchema.parse(res.data);
}

export async function upsertMyTutorProfile(
  accessToken: string,
  body: CreateTutorProfileInput,
): Promise<TutorProfileResponse> {
  const res = await apiCall<unknown>('/tutors/me', {
    method: 'POST',
    accessToken,
    body,
  });
  return TutorProfileResponseSchema.parse(res.data);
}

export async function patchMyTutorProfile(
  accessToken: string,
  body: UpdateTutorProfileInput,
): Promise<TutorProfileResponse> {
  const res = await apiCall<unknown>('/tutors/me', {
    method: 'PATCH',
    accessToken,
    body,
  });
  return TutorProfileResponseSchema.parse(res.data);
}

export async function fetchOnboardingStatus(
  accessToken: string,
): Promise<OnboardingStatus> {
  const res = await apiCall<unknown>('/tutors/me/onboarding-status', {
    accessToken,
  });
  return OnboardingStatusSchema.parse(res.data);
}

export async function signTutorPhotoUpload(
  accessToken: string,
  body: PhotoUploadRequestInput,
): Promise<PhotoUploadResponse> {
  const res = await apiCall<unknown>('/tutors/me/photo', {
    method: 'POST',
    accessToken,
    body,
  });
  return PhotoUploadResponseSchema.parse(res.data);
}

export async function confirmTutorPhoto(
  accessToken: string,
  objectPath: string,
): Promise<TutorProfileResponse> {
  const res = await apiCall<unknown>('/tutors/me/photo/confirm', {
    method: 'POST',
    accessToken,
    body: { objectPath },
  });
  return TutorProfileResponseSchema.parse(res.data);
}

export async function searchTutors(
  accessToken: string,
  params: Partial<TutorSearchQuery> = {},
): Promise<TutorSearchResponse> {
  const qs = new URLSearchParams();
  if (params.subject) qs.set('subject', params.subject);
  if (params.gradeLevel) qs.set('gradeLevel', params.gradeLevel);
  if (params.name) qs.set('name', params.name);
  if (params.language) qs.set('language', params.language);
  if (params.gender) qs.set('gender', params.gender);
  if (params.minHourlyInr != null) qs.set('minHourlyInr', String(params.minHourlyInr));
  if (params.maxHourlyInr != null) qs.set('maxHourlyInr', String(params.maxHourlyInr));
  if (params.page != null) qs.set('page', String(params.page));
  if (params.pageSize != null) qs.set('pageSize', String(params.pageSize));
  const query = qs.toString();
  const res = await apiCall<unknown>(`/tutors/search${query ? `?${query}` : ''}`, { accessToken });
  try {
    return TutorSearchResponseSchema.parse(res.data);
  } catch (err) {
    console.error('[tutor-api] Schema parse error:', err);
    console.error('[tutor-api] API response data:', res.data);
    throw err;
  }
}

export async function fetchPublicTutor(
  accessToken: string,
  tutorId: string,
): Promise<PublicTutorView> {
  const res = await apiCall<unknown>(`/tutors/${tutorId}`, { accessToken });
  return PublicTutorViewSchema.parse(res.data);
}
